import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Minus, ArrowRight, Sparkles, AlertTriangle, Crown, ChevronRight } from "lucide-react";
import MiniPriceChart from "@/components/MiniPriceChart";
import { Button } from "@/components/ui/button";
import { DecisionStrip } from "@/components/DecisionStrip";
import { fmtPrice, fmtCompact } from "@/lib/format";
import { getStatusTokens, getDisplayTokens, getSignalTokens, statusHeadline } from "@/lib/status";
import CompanyLogo from "@/components/common/CompanyLogo";
import OpportunityScoreScale, { OpportunityScoreScaleInfo } from "@/components/OpportunityScoreScale";
import { useTranslation } from "@/lib/i18n";

/**
 * PickCard — stock pick card used in the "AI Market Snapshot" grid.
 *
 * Variants:
 *  - default : standard card (used on desktop grid + non-featured mobile picks if not using row)
 *  - featured: lead/hero card with stronger glow, larger numbers, top-edge accent
 *  - row     : ultra-compact horizontal list-row card (mobile secondary picks)
 *
 * Color follows the SIGNAL (Opportunity / Neutral / Risk).
 *
 * Phase 8.LP.LR — optional `identity` prop (from
 * ``useCompanyIdentities``) enables real company logos on the card
 * header. Falls back to the ticker text tile when no identity is
 * cached — the fallback matches the original design so a cold cache
 * looks like the previous release.
 */
export default function PickCard({ pick, identity, index = 0, variant = "default" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const v = pick.valuation;
  const signalTok = getDisplayTokens(v);
  const statusTok = getStatusTokens(v.status);
  const headline = statusHeadline(v.status, v.upside_pct);
  const Up = v.upside_pct > 0 ? ArrowUpRight : v.upside_pct < 0 ? ArrowDownRight : Minus;
  const signalKey = v.signal || (pick.is_opportunity ? "Opportunity" : v.status === "Fair" ? "Neutral" : "Risk");
  // Phase 6.4 / Correction Block 2 (finding #5) — Canonical
  // classification is the ONLY source of truth for the Top
  // Opportunity treatment.  Legacy ``is_top_pick`` / homepage slot
  // position no longer promotes a card on their own; the backend's
  // classify() guard already enforces every hard requirement (score
  // ≥ 75, upside ≥ 15%, risk not High, positive FCF avg, Fair Value
  // Confidence Medium/High, no active veto, quality gate pass,
  // reconciled valuation, no outlier).
  const isTopPick = pick.classification === "top_opportunity";
  const delta = v.fair_value_delta != null ? v.fair_value_delta : (v.fair_value - pick.price);
  const deltaSign = delta >= 0 ? "+" : "−";
  const isFeatured = variant === "featured";
  const isRow = variant === "row";

  // ROW variant: tight horizontal mobile list-row card
  if (isRow) {
    return (
      <motion.button
        type="button"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        onClick={() => navigate(`/analyze/${pick.ticker}`)}
        data-testid="pick-card"
        data-variant="row"
        data-status={v.status}
        data-signal={signalKey}
        className="group relative w-full max-w-full min-w-0 overflow-hidden rounded-xl border bg-card/45 px-3.5 py-3 text-left transition-all active:scale-[0.99] hover:bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-glow-blue)]"
        style={{ borderColor: "rgba(45,46,55,0.85)" }}
      >
        {/* Whisper of signal color on the left edge */}
        <span
          className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full"
          style={{ background: signalTok.fg, opacity: 0.55 }}
          aria-hidden
        />
        <div className="flex items-center gap-3">
          {/* Phase 8.LP.LR — logo tile when available, ticker text
              otherwise. Logo is provider image, unmodified. */}
          {identity && identity.logo_url ? (
            <CompanyLogo
              ticker={pick.ticker}
              name={identity.name || pick.name}
              logoUrl={identity.logo_url}
              size="md"
              testId={`pick-logo-${pick.ticker}`}
              className="!h-10 !w-10 !rounded-lg"
            />
          ) : (
            <div className="w-10 h-10 shrink-0 rounded-lg bg-secondary/55 border border-border/70 flex items-center justify-center font-mono text-[10.5px] tabular tracking-wider">
              {pick.ticker}
            </div>
          )}
          {/* Middle: name + headline */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-medium leading-tight truncate">{pick.name}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] min-w-0 overflow-hidden whitespace-nowrap">
              <span
                className="inline-flex items-center gap-0.5 font-medium shrink-0"
                style={{ color: signalTok.fg }}
                data-testid="pick-headline"
              >
                <Up className="w-3 h-3" />
                {headline}
              </span>
              <span className="text-muted-foreground/40 shrink-0">·</span>
              <span className="font-mono tabular text-muted-foreground shrink-0">${fmtPrice(pick.price)}</span>
              <span className="text-muted-foreground/40 shrink-0">→</span>
              <span className="font-mono tabular shrink-0" style={{ color: statusTok.fg }}>${fmtPrice(v.fair_value)}</span>
            </div>
          </div>
          {/* Right: score block */}
          <div className="shrink-0 flex items-center gap-2">
            <div className="text-right leading-none">
              <div
                className="font-display text-[22px] tabular leading-none"
                style={{ color: signalTok.fg }}
                data-testid="pick-ai-score"
              >
                {v.ai_score}
              </div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.14em] text-muted-foreground/70">Score</div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>
      </motion.button>
    );
  }

  // DEFAULT / FEATURED full card
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      data-testid="pick-card"
      data-variant={variant}
      data-status={v.status}
      data-signal={signalKey}
      data-top-pick={isTopPick ? "true" : undefined}
      onClick={() => navigate(`/analyze/${pick.ticker}`)}
      className={`relative overflow-hidden rounded-2xl border bg-card/60 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-[var(--app-shadow-elev-2)] focus-within:ring-2 focus-within:ring-[color:var(--app-glow-blue)] w-full max-w-full min-w-0 flex flex-col h-full ${isFeatured ? "p-4 sm:p-5" : "p-3 sm:p-5"} ${isTopPick || isFeatured ? "ring-1 ring-[rgba(63,174,137,0.35)]" : ""}`}
      style={{
        borderColor: isFeatured || isTopPick ? "rgba(63,174,137,0.55)" : signalTok.border,
        boxShadow: isFeatured
          ? "0 1px 0 rgba(255,255,255,0.05), 0 22px 60px -28px rgba(63,174,137,0.55), 0 30px 80px -40px rgba(0,0,0,0.55)"
          : undefined,
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isFeatured || isTopPick
            ? "radial-gradient(640px 200px at 0% 0%, rgba(63,174,137,0.34), transparent 60%), radial-gradient(440px 180px at 100% 0%, rgba(63,174,137,0.14), transparent 70%)"
            : `radial-gradient(420px 140px at 0% 0%, ${signalTok.glow}, transparent 70%)`,
        }}
      />
      {(isTopPick || isFeatured) ? (
        <div
          className="absolute top-0 left-3 right-3 h-px pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(63,174,137,0.75), transparent)" }}
        />
      ) : null}

      <div className="relative flex items-start gap-2.5 sm:gap-3">
        {/* Phase 8.LP.LR — real logo when cached, ticker text tile
            otherwise. The image is provider-supplied and rendered
            unmodified (`object-contain`); we never crop or recolor. */}
        {identity && identity.logo_url ? (
          <CompanyLogo
            ticker={pick.ticker}
            name={identity.name || pick.name}
            logoUrl={identity.logo_url}
            size={isFeatured ? "lg" : "md"}
            testId={`pick-logo-${pick.ticker}`}
            className={`${isFeatured ? "!h-10 !w-10 sm:!h-11 sm:!w-11 !rounded-lg" : "!h-9 !w-9 sm:!h-10 sm:!w-10 !rounded-lg"}`}
          />
        ) : (
          <div className={`shrink-0 rounded-lg bg-secondary/60 border border-border/70 flex items-center justify-center font-mono ${isFeatured ? "w-10 h-10 sm:w-11 sm:h-11 text-[12px] sm:text-sm" : "w-9 h-9 sm:w-10 sm:h-10 text-[11px] sm:text-xs"}`}>
            {pick.ticker}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className={`font-medium leading-tight truncate ${isFeatured ? "text-[14px] sm:text-[15px]" : "text-[13px] sm:text-sm"}`}>{pick.name}</div>
          <div className="text-[11px] text-muted-foreground truncate mt-0.5">{pick.sector}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {/* Phase 9.HMS.v1 — Top Opportunity badge is only shown when
              the pick actually qualifies (is_top_pick=true), never
              merely because the card is the featured/lead card. Falls
              back to the standard category badge otherwise. */}
          {isTopPick ? <TopPickBadge /> : <CategoryBadge signalKey={signalKey} />}
        </div>
      </div>

      <div className="relative mt-3 sm:mt-4">
        {/* Sub-pass 2A follow-up (2026-02-18) — the info trigger now
            lives right beside the "AI Opportunity Score" label so it
            never reads as a scale midpoint marker.  The scale itself
            keeps its numeric 0 / 50 / 100 endpoints below. */}
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>AI Opportunity Score</span>
          <OpportunityScoreScaleInfo testId="pick-score-info" />
        </div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span
            className={`font-display tabular leading-none ${isFeatured ? "text-[44px] sm:text-5xl" : "text-[34px] sm:text-5xl"}`}
            style={{ color: signalTok.fg, textShadow: isFeatured ? `0 0 30px ${signalTok.glow}` : undefined }}
            data-testid="pick-ai-score"
          >
            {v.ai_score}
          </span>
          <span className="text-xs text-muted-foreground tabular">/100</span>
        </div>
        {/* Sub-pass 2A (2026-02-17) — subtle 0-100 scale mirroring
            the canonical score-only bands from valuation_guards.py.
            Purely presentational, respects the premium dark palette,
            and never asserts a final classification on its own. */}
        <div className="mt-2" data-testid="pick-score-scale-wrap">
          <OpportunityScoreScale
            score={v.ai_score}
            testId="pick-score-scale"
            markerColor={signalTok.fg}
            compact={!isFeatured}
          />
        </div>
        {/* Headline gets its own line so it can never be truncated by a narrow
            grid column on desktop. Mirrors mobile's stacked layout. */}
        <div
          className={`mt-1.5 inline-flex items-center gap-1 font-display tracking-tight leading-tight ${isFeatured ? "text-[14px] sm:text-[15px]" : "text-[13px] sm:text-[14px]"}`}
          style={{ color: signalTok.fg }}
          data-testid="pick-headline"
        >
          <Up className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" /> {headline}
        </div>
      </div>

      <div className="relative mt-2 sm:mt-3 flex flex-col gap-1 min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Price → Fair Value</span>
        <div className="font-mono tabular leading-tight text-[12.5px] sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis" data-testid="pick-price-fv">
          <span className="text-foreground/90">${fmtPrice(pick.price)}</span>
          <span className="text-muted-foreground mx-1">→</span>
          <span style={{ color: statusTok.fg }}>${fmtPrice(v.fair_value)}</span>
          <span className="ml-1.5 text-[10.5px] tabular" style={{ color: statusTok.fg }} data-testid="pick-dollar-delta">
            ({deltaSign}${fmtPrice(Math.abs(delta))})
          </span>
        </div>
      </div>

      <DecisionStrip valuation={v} compact className="relative mt-2.5 sm:mt-3" />

      <div className="relative mt-auto pt-3 -mx-1">
        <div className={isFeatured ? "h-[72px] sm:h-[88px]" : "h-[52px] sm:h-[88px]"}>
          <MiniPriceChart
            ticker={pick.ticker}
            fairValue={v.fair_value}
            fairLow={v.fair_value_low}
            fairHigh={v.fair_value_high}
            color={isFeatured ? "#E9F8F2" : "#DDE1E4"}
            // Phase 9.HMS.v1 — pass the snapshot-embedded sparkline
            // series so MiniPriceChart never triggers `/api/stocks/
            // {ticker}/prices` on the public Homepage. Always
            // an array (may be empty on pre-launch tickers with no
            // cached chart, in which case the chart renders empty
            // safely).
            series={Array.isArray(pick.chart_series) ? pick.chart_series : []}
          />
        </div>
      </div>

      <div className="relative mt-2 sm:mt-3 flex items-center justify-end text-[11px] text-muted-foreground">
        {/* Phase 6.BI-12 follow-up — public "Mode:" badge removed entirely
            from PickCards. It looked like a dev artifact on the marketing
            homepage. The valuation `mode` value is still available via
            Owner Console diagnostics + cached payloads, and admins can
            inspect it on the Analyze page when needed. */}
        <span className="hidden sm:inline tabular">Mkt cap {fmtCompact(pick.market_cap)}</span>
      </div>

      <Button
        size="sm"
        variant={signalKey === "Opportunity" ? "default" : "outline"}
        className="relative mt-2.5 sm:mt-3 w-full h-10"
        onClick={(e) => { e.stopPropagation(); navigate(`/analyze/${pick.ticker}`); }}
        data-testid="pick-analyze-button"
      >
        Open analysis <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>

      {/* Correction Block 2 (finding #7) — Public homepage-card
          research metadata.  Order and copy are the OWNER-approved
          final:
             Data as of:        <ws7_metadata.data_as_of>
             Analysis updated:  <ws7_metadata.last_updated_at
                                 || ws7_metadata.analysis_generated_at>
             Model:             <ws7_metadata.model_version
                                 || stockaily.deterministic_valuation.v1>
             Snapshot published:<pick.snapshot_published_at>

          The old "Model: Example preview" line and the
          "Example analysis · " prefix on the provenance line have
          been removed — they added no information and made the
          real model version harder to see.  Every row renders only
          when its underlying value is present so a legacy snapshot
          missing one field never displays a blank line, and we
          never substitute the current browser time for a missing
          persisted timestamp. */}
      <PickCardMetadata pick={pick} />
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// PickCardMetadata — public homepage-card research metadata block.
// Renders 4 lines in order using persisted timestamps only.  Any row
// whose underlying value is missing is silently omitted; we do NOT
// substitute the current wall clock for a missing persisted value.
// ---------------------------------------------------------------------------
function PickCardMetadata({ pick }) {
  const { t } = useTranslation();
  const md = pick.ws7_metadata || {};
  const fmt = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return null; }
  };
  const dataAsOf = fmt(md.data_as_of);
  const analysisUpdated = fmt(md.last_updated_at || md.analysis_generated_at);
  const model = md.model_version || "stockaily.deterministic_valuation.v1";
  const snapshotPublished = fmt(pick.snapshot_published_at);
  const row = "relative mt-1 text-[10.5px] text-muted-foreground/70";
  return (
    <>
      {dataAsOf ? (
        <div className={`${row} mt-2.5`} data-testid="pick-card-data-as-of">
          {(t("landing.pick_data_as_of") || "Data as of: {date}").replace("{date}", dataAsOf)}
        </div>
      ) : null}
      {analysisUpdated ? (
        <div className={row} data-testid="pick-card-analysis-updated">
          {(t("landing.pick_analysis_updated") || "Analysis updated: {date}").replace("{date}", analysisUpdated)}
        </div>
      ) : null}
      <div className={row} data-testid="pick-card-model-line">
        {(t("landing.pick_model") || "Model: {model}").replace("{model}", model)}
      </div>
      {snapshotPublished ? (
        <div className={row} data-testid="pick-card-snapshot-provenance">
          {(t("landing.pick_snapshot_published") || "Snapshot published: {date}").replace("{date}", snapshotPublished)}
        </div>
      ) : null}
    </>
  );
}

function CategoryBadge({ signalKey }) {
  const cfg = getSignalTokens(signalKey);
  const Icon = signalKey === "Opportunity" ? Sparkles : signalKey === "Risk" ? AlertTriangle : Minus;
  const testId = signalKey === "Opportunity"
    ? "pick-opportunity-badge"
    : signalKey === "Risk"
    ? "pick-risk-badge"
    : "pick-neutral-badge";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
      style={{ color: cfg.fg, background: cfg.bg, border: `1px solid ${cfg.border}` }}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" /> {cfg.label()}
    </span>
  );
}

/**
 * Premium — but understated — "Top Opportunity" badge.
 */
function TopPickBadge() {
  return (
    <span
      className="relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap overflow-hidden"
      style={{
        color: "#E9F8F2",
        background: "linear-gradient(135deg, rgba(63,174,137,0.32), rgba(63,174,137,0.18) 60%, rgba(224,179,65,0.18))",
        border: "1px solid rgba(63,174,137,0.65)",
        boxShadow: "0 0 0 1px rgba(63,174,137,0.18), 0 6px 22px -10px rgba(63,174,137,0.55)",
      }}
      data-testid="pick-top-opportunity-badge"
      title="Top Opportunity — high score, strong upside, manageable risk"
    >
      <Crown className="w-3 h-3" style={{ color: "#F6D274" }} />
      Top Opportunity
    </span>
  );
}
